// Source quality tiering, shared by the article UI and the catalogue migration.
//
// Tier 1 - peer-reviewed research, government/scientific agencies, universities.
// Tier 2 - museums, scientific organisations, established reference institutions.
// Tier 3 - science journalism that identifies its own evidence.
// Tier 0 - social/discovery provenance (Instagram, TikTok, YouTube, Reddit...).
//          Useful as "Inspired by", never as evidence.

export type SourceItem = { url: string; title?: string | null };
export type Tier = 0 | 1 | 2 | 3;

const SOCIAL =
  /(^|\.)(instagram\.com|facebook\.com|fb\.watch|threads\.net|tiktok\.com|youtube\.com|youtu\.be|reddit\.com|x\.com|twitter\.com|pinterest\.[a-z.]+)$/i;

const TIER1 =
  /(\.gov$|\.gov\.[a-z]{2}$|\.edu$|\.ac\.[a-z]{2}$|(^|\.)(nasa|noaa|nih|ncbi\.nlm\.nih|pubmed\.ncbi\.nlm\.nih|europepmc|nature|science|sciencedirect|springer|wiley|cell|plos|pnas|bmj|thelancet|frontiersin|biorxiv|arxiv|jstor|royalsocietypublishing|usgs|nsf|esa|cern|who|cdc|ipcc|niwa|csiro|bom\.gov)\.)/i;

const TIER2 =
  /(^|\.)(wikipedia\.org|britannica\.com|si\.edu|amnh\.org|nhm\.ac\.uk|mba\.ac\.uk|mbari\.org|whoi\.edu|zsl\.org|iucnredlist\.org|worldwildlife\.org|audubon\.org|oceana\.org|australian\.museum|fieldmuseum\.org|nationalgeographic\.com|nps\.gov|kew\.org|rspb\.org\.uk|antarctica\.gov\.au)$/i;

const TIER3 =
  /(^|\.)(sciencenews\.org|newscientist\.com|scientificamerican\.com|quantamagazine\.org|smithsonianmag\.com|livescience\.com|space\.com|bbc\.co\.uk|bbc\.com|nytimes\.com|theguardian\.com|reuters\.com|apnews\.com|arstechnica\.com|phys\.org|eurekalert\.org|sciencealert\.com)$/i;

export function hostOf(url: string): string | null {
  try {
    return new URL(url).hostname.replace(/^www\./, "").toLowerCase();
  } catch {
    return null;
  }
}

export function tierOf(url: string): Tier {
  const host = hostOf(url);
  if (!host) return 0;
  if (SOCIAL.test(host)) return 0;
  if (TIER1.test(host) || TIER1.test(`${host}.`)) return 1;
  if (TIER2.test(host)) return 2;
  if (TIER3.test(host)) return 3;
  return 3;
}

export function isSocial(url: string): boolean {
  const host = hostOf(url);
  return !!host && SOCIAL.test(host);
}

/** Malformed record: no usable URL, or a literal "undefined"/"null" leaking in. */
export function isMalformed(s: SourceItem | null | undefined): boolean {
  const url = String(s?.url ?? "").trim();
  if (!url || !hostOf(url)) return true;
  return /^(undefined|null)$/i.test(url);
}

export type SourceAnalysis = {
  evidence: SourceItem[]; // non-social, usable
  inspiredBy: SourceItem[]; // social provenance
  malformedTitles: number; // "undefined"/"null" titles
  independentDomains: number;
  bestTier: Tier | null;
  /** unsourced | social_only | single | weak | ok */
  state: "unsourced" | "social_only" | "single" | "weak" | "ok";
};

export function analyzeSources(raw: unknown): SourceAnalysis {
  const list: SourceItem[] = Array.isArray(raw)
    ? (raw as any[]).map((s) => ({ url: String(s?.url ?? ""), title: s?.title ?? null }))
    : [];

  const evidence: SourceItem[] = [];
  const inspiredBy: SourceItem[] = [];
  let malformedTitles = 0;

  for (const s of list) {
    if (/^(undefined|null)$/i.test(String(s.title ?? "").trim())) malformedTitles++;
    if (isMalformed(s)) continue;
    if (isSocial(s.url)) inspiredBy.push(s);
    else evidence.push(s);
  }

  const domains = new Set(evidence.map((s) => hostOf(s.url)!).filter(Boolean));
  const tiers = evidence.map((s) => tierOf(s.url));
  const bestTier = tiers.length ? (Math.min(...tiers) as Tier) : null;

  let state: SourceAnalysis["state"];
  if (list.length === 0) state = "unsourced";
  else if (evidence.length === 0) state = "social_only";
  else if (domains.size === 1) state = "single";
  else if (bestTier !== null && bestTier >= 3) state = "weak";
  else state = "ok";

  return {
    evidence,
    inspiredBy,
    malformedTitles,
    independentDomains: domains.size,
    bestTier,
    state,
  };
}

/**
 * Claim-risk topics. An unsourced piece about human biology, medicine, death,
 * disasters or precise historical numbers can damage credibility far faster
 * than one about an animal's odd morphology.
 */
const RISK_PATTERNS: Array<[RegExp, number]> = [
  [/\b(medic|disease|cancer|vaccine|drug|toxic|poison|dose|symptom|treatment|virus|infection)\w*/i, 35],
  [/\b(die|died|dies|death|deadly|fatal|kill|killed|lethal|survive|survival)\w*/i, 25],
  [/\b(nutrition|diet|calorie|supplement|vitamin|fasting|sugar|protein)\w*/i, 25],
  [/\b(brain|blood|heart|lung|organ|dna|gene|hormone|pregnan|baby|babies|immune)\w*/i, 20],
  [/\b(tornado|hurricane|earthquake|tsunami|volcan|flood|wildfire|disaster|radiation|nuclear)\w*/i, 25],
  [/\b(quantum|relativ|particle|black hole|universe|physics|energy|antimatter)\w*/i, 15],
  [/\b(climate|pollut|extinct|emission|ozone|microplastic)\w*/i, 15],
  [/\b(18\d\d|19\d\d|20[0-2]\d)\b/, 10], // precise historical years
  [/\b\d+(\.\d+)?\s?(mph|km\/h|miles|kilometres|kilometers|tons|tonnes|percent|%|degrees)\b/i, 10],
];

export function riskScore(title: string, body: string): number {
  const hay = `${title}\n${body.slice(0, 4000)}`;
  let score = 0;
  for (const [re, w] of RISK_PATTERNS) if (re.test(hay)) score += w;
  return Math.min(100, score);
}

/** Overconfident phrasing that the migration should flag for softening. */
export const OVERCLAIM_PATTERNS: RegExp[] = [
  /\b(the (largest|biggest|fastest|deadliest|strongest|oldest|only) (ever|on record|in history))\b/i,
  /\b(scientists (now )?know|proves|proven|definitely|certainly|undeniably|always|never fails)\b/i,
  /\b(this is why|the reason is) (it|they|we) (die|evolved|exist)\b/i,
  /\bboils? from the inside out\b/i,
  /\b(100|zero) percent of\b/i,
];

export function overclaims(body: string): string[] {
  const hits: string[] = [];
  for (const re of OVERCLAIM_PATTERNS) {
    const m = body.match(re);
    if (m) hits.push(m[0]);
  }
  return hits;
}

/** Broken copy the generator leaked: glued words, truncated snippets. */
export function copyDefects(body: string): string[] {
  const out: string[] = [];
  if (/…\s*$/.test(body.trim())) out.push("body ends truncated");
  if (/\b\w{4,}(?:for|and|the|with|that|from)\b(?=[a-z])/.test(body) && /[a-z]{8,}/.test(body)) {
    const m = body.match(/\b[a-z]{3,}(?:for|and|the|with|that|from)[a-z]{3,}\b/);
    if (m) out.push(`possible missing space: "${m[0]}"`);
  }
  if (/\b(undefined|null|NaN)\b/.test(body)) out.push("placeholder token in body");
  return out;
}
